import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {runAccountSearch} from 'src/openapi/admin/apis/RunAccountSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {accountSearchResultStateSelector} from '../slices/account-search-result-state.slice';
import {
    accountSearchResultActions,
    accountSearchResultDataSelector,
    accountSearchResultResultSelector,
} from '../slices/account-search-result.slice';

import type {AccountSearchResultCacheKeys} from '../typedef';
import type {AccountSearchCriteria} from 'src/openapi/admin/models/AccountSearchCriteria';
import type {AccountSearchFiltering} from 'src/openapi/admin/models/AccountSearchFiltering';
import type {AccountSearchResponse} from 'src/openapi/admin/models/AccountSearchResponse';


export const useGetAccountSearchResult = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: AccountSearchResultCacheKeys) => apiDispatch(runAccountSearch(
        {
            accountSearchRequest: {
                searchCriteria: cacheKeys.searchCriteria as AccountSearchCriteria,
                filtering: cacheKeys.filters as AccountSearchFiltering,
                sortCriteria: {
                    columnId: cacheKeys.sortCriteria.field,
                    direction: cacheKeys.sortCriteria.direction,
                },
                pagination: cacheKeys.pagination,
            },
        },
    )), [apiDispatch]);

    const setLoadedData = useCallback((
        response: AccountSearchResponse,
        cacheKeys: AccountSearchResultCacheKeys,
    ) => {
        dispatch(accountSearchResultActions.onSuccess(({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.result,
        })));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: accountSearchResultDataSelector,
        resultSelector: accountSearchResultResultSelector,
        stateSelector: accountSearchResultStateSelector,
        actions: accountSearchResultActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
